// Prompt 0454 — AdminPuestoPanel orquestador (reemplaza al monolitico de 1473L del 0453).
// Responsabilidades:
//   - layout overlay + cabecera (titulo, codigo, tabs)
//   - estado del tab activo
//   - cierre ESC / click-fuera con dirty-state guard (DirtyStateWarning)
//   - carga del detalle via usePuestoData (compartido entre tabs)
//   - dirty state centralizado via useDirtyTabs
// Toda la logica de renderizado y negocio vive en puesto-panel/tabs/*.

import { useState } from 'react';
import { useI18n } from '../../../../../i18n';
import { LoadingSpinner } from '../../../../shared/components/LoadingSpinner';
import { AdminOverlayPanel } from './shared/AdminOverlayPanel';
import { DatosTab, type AdminPuestoPanelInput } from './puesto-panel/tabs/DatosTab';
import { AsignacionesTab } from './puesto-panel/tabs/AsignacionesTab';
import { OrganigramaTab } from './puesto-panel/tabs/OrganigramaTab';
import { FichaTab } from './puesto-panel/tabs/FichaTab';
import { RolesGobernanzaTab } from './puesto-panel/tabs/RolesGobernanzaTab';
import { DirtyStateWarning } from './ficha-shared/DirtyStateWarning';
import { useDirtyTabs } from './ficha-shared/useDirtyTabs';

type TabId = 'datos' | 'asignaciones' | 'organigrama' | 'ficha' | 'rolesGobernanza';
import { usePuestoData } from './puesto-panel/hooks/usePuestoData';

export type { AdminPuestoPanelInput };

interface AdminPuestoPanelProps {
  puesto: AdminPuestoPanelInput | null; // null = crear nuevo
  onClose: () => void;
  onSaved: () => void;
}

export function AdminPuestoPanel({ puesto, onClose, onSaved }: AdminPuestoPanelProps) {
  const { t } = useI18n();
  const adm = (t.structure as Record<string, unknown>)?.admin as
    | Record<string, unknown>
    | undefined;
  const lbl = (adm ?? {}) as Record<string, string>;
  const panel = (lbl.puestoPanel ?? {}) as unknown as Record<string, string>;

  const isCrear = puesto === null;
  const [activeTab, setActiveTab] = useState<TabId>('datos');
  const [showDirtyWarning, setShowDirtyWarning] = useState(false);

  const dirty = useDirtyTabs<TabId>();
  const {
    detalle,
    loading: loadingDetalle,
    refetch: refetchDetalle,
  } = usePuestoData(isCrear ? null : (puesto?.id ?? null));

  // Escape gestionado por AdminOverlayPanel (desde 0483). Guard de dirty-state
  // aplicado via requestClose que se pasa como onClose al overlay.

  const TABS: { key: TabId; label: string }[] = [
    { key: 'datos', label: panel.tabDatos ?? 'Datos' },
    { key: 'asignaciones', label: panel.tabAsignaciones ?? 'Asignaciones' },
    { key: 'organigrama', label: panel.tabOrganigrama ?? 'Organigrama' },
    { key: 'ficha', label: panel.tabFicha ?? 'Ficha' },
    { key: 'rolesGobernanza', label: panel.tabRolesGobernanza ?? 'Roles de gobernanza' },
  ];

  function requestClose() {
    if (dirty.anyDirty()) {
      setShowDirtyWarning(true);
    } else {
      onClose();
    }
  }

  function confirmDiscardAndClose() {
    dirty.markAllClean();
    setShowDirtyWarning(false);
    onClose();
  }

  function tabLabel(id: TabId): string {
    const tab = TABS.find((x) => x.key === id);
    return tab?.label ?? id;
  }

  const tituloMostrado =
    !isCrear && detalle?.titulo
      ? detalle.titulo
      : !isCrear && puesto?.titulo
        ? puesto.titulo
        : (panel.nuevoPuesto ?? 'Nuevo puesto');
  const codigoMostrado =
    !isCrear && (detalle?.codigo || puesto?.codigo) ? (detalle?.codigo ?? puesto?.codigo) : null;

  return (
    <AdminOverlayPanel open={true} onClose={requestClose}>
      <>
        {/* Cabecera */}
        <div
          style={{
            flexShrink: 0,
            padding: '24px 32px 0',
            borderBottom: '1px solid #E2E6ED',
            background: '#fff',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              marginBottom: 12,
            }}
          >
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#1F3864' }}>
              {tituloMostrado}
            </h2>
            {codigoMostrado && (
              <span
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#5A6171',
                  padding: '3px 10px',
                  background: '#F0F4FA',
                  borderRadius: 10,
                }}
              >
                {codigoMostrado}
              </span>
            )}
          </div>

          <div style={{ display: 'flex' }}>
            {TABS.map((tab) => {
              const disabled = isCrear && tab.key !== 'datos';
              const active = activeTab === tab.key;
              const tabIsDirty = dirty.isDirty(tab.key);
              return (
                <button
                  key={tab.key}
                  disabled={disabled}
                  onClick={() => !disabled && setActiveTab(tab.key)}
                  style={{
                    padding: '10px 20px',
                    border: 'none',
                    background: 'transparent',
                    fontSize: 13,
                    fontWeight: active ? 700 : 500,
                    color: disabled ? '#C4C9D1' : active ? '#1F3864' : '#5A6171',
                    borderBottom: active ? '2px solid #1F3864' : '2px solid transparent',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    fontFamily: 'inherit',
                    marginBottom: -1,
                  }}
                >
                  {tab.label}
                  {tabIsDirty && (
                    <span
                      style={{
                        marginLeft: 6,
                        color: '#DC2626',
                        fontSize: 18,
                        lineHeight: 1,
                      }}
                      aria-label="unsaved"
                    >
                      ·
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Contenido scrollable */}
        <div style={{ flex: 1, overflow: 'auto', padding: '20px 32px 24px' }}>
          {activeTab === 'datos' && (
            <DatosTab
              puesto={puesto}
              detalle={detalle}
              onSaved={async (nuevoPuestoId) => {
                onSaved();
                dirty.markClean('datos');
                if (nuevoPuestoId) {
                  onClose();
                } else {
                  await refetchDetalle();
                }
              }}
            />
          )}

          {activeTab === 'asignaciones' &&
            (loadingDetalle ? (
              <LoadingSpinner size="sm" muted />
            ) : detalle ? (
              <AsignacionesTab detalle={detalle} onRefresh={refetchDetalle} />
            ) : null)}

          {activeTab === 'organigrama' &&
            (loadingDetalle ? (
              <LoadingSpinner size="sm" muted />
            ) : detalle ? (
              <OrganigramaTab
                detalle={detalle}
                isDirty={dirty.isDirty('organigrama')}
                onMarkDirty={() => dirty.markDirty('organigrama')}
                onMarkClean={() => dirty.markClean('organigrama')}
                onPuestoUpdated={refetchDetalle}
              />
            ) : null)}

          {activeTab === 'ficha' && !isCrear && puesto?.id && (
            <FichaTab
              puestoId={puesto.id}
              hasFicha={!!(detalle?.rolFuncional?.fichaDocumentoId ?? puesto.fichaDocumentoId)}
              isDirty={dirty.isDirty('ficha')}
              onMarkDirty={() => dirty.markDirty('ficha')}
              onMarkClean={() => dirty.markClean('ficha')}
              onCreated={refetchDetalle}
            />
          )}

          {activeTab === 'rolesGobernanza' && !isCrear && puesto?.id && (
            <RolesGobernanzaTab puestoId={puesto.id} onChanged={refetchDetalle} />
          )}
        </div>

        <DirtyStateWarning
          open={showDirtyWarning}
          tabsDirtyLabels={dirty.dirtyTabIds().map(tabLabel)}
          onConfirm={confirmDiscardAndClose}
          onCancel={() => setShowDirtyWarning(false)}
        />
      </>
    </AdminOverlayPanel>
  );
}
